import { FC, useMemo } from "react";

import { Band } from "shared/assets/icons";
import { Card } from "shared/components";
import { copy } from "shared/assets";
import { IBand, IBandSubscription, SubscriptionStatus } from "Bands/types";
import { BoldText, MutedText, SmallText } from "shared/styles";
import { Spacer } from "shared/layout";
import { formatDateTime, TimeFormat } from "shared/utils";

const bandCopy = copy.adminDashboard.band;

interface Props {
  band: IBand;
}

export const BandInfo: FC<Props> = ({ band }) => {
  const activePlan = useMemo(() => {
    const plan = band.subscription;

    if (plan?.status === SubscriptionStatus.active) {
      return plan;
    }

    return null;
  }, [band.subscription]);

  const upcomingPlan = useMemo(() => {
    const plan = band.subscription;

    if (!plan) return null;

    switch (plan.status) {
      case SubscriptionStatus.active: {
        if (!plan.nextPhasePlan || !plan.nextPhaseStart) return null;

        return {
          plan: plan.nextPhasePlan,
          start: plan.nextPhaseStart,
        } as Pick<IBandSubscription, "plan" | "start">;
      }
      case SubscriptionStatus.inactive: {
        return {
          plan: plan.plan,
          start: plan.start,
        } as Pick<IBandSubscription, "plan" | "start">;
      }
      default:
        return null;
    }
  }, [band.subscription]);

  const renderItem = (label: string, value: string | JSX.Element) => (
    <div>
      <BoldText>{label}:</BoldText> {value}
    </div>
  );

  const renderActivePlan = useMemo(() => {
    if (!activePlan) return null;

    const value = (
      <span>
        {activePlan.plan}
        <SmallText as={MutedText}>
          {" "}
          (
          {formatDateTime(activePlan.start, {
            dateOnly: true,
          })}
          {" - "}
          {formatDateTime(activePlan.stop, {
            dateOnly: true,
          })}
          )
        </SmallText>
      </span>
    );

    return <>{renderItem(bandCopy.fields.activePlan, value)}</>;
  }, [activePlan]);

  const renderUpcomingPlan = useMemo(() => {
    if (!upcomingPlan) return null;

    const value = (
      <span>
        {upcomingPlan.plan}
        <SmallText as={MutedText}>
          {" "}
          (starts on{" "}
          {formatDateTime(upcomingPlan.start, {
            dateOnly: true,
          })}
          )
        </SmallText>
      </span>
    );

    return <>{renderItem(bandCopy.fields.upcomingPlan, value)}</>;
  }, [upcomingPlan]);

  const renderCreatedAt = useMemo(() => {
    const formatted = formatDateTime(band.createdAt, {
      timeFormat: TimeFormat.twentyFour,
    });

    if (formatted)
      return <>{renderItem(bandCopy.fields.createdAt, formatted)}</>;

    return null;
  }, [band.createdAt]);

  return (
    <Card title={band?.title} icon={Band}>
      <Spacer size="s">
        {renderItem(bandCopy.fields.bandId, band.id)}
        {renderCreatedAt}
        {renderActivePlan}
        {renderUpcomingPlan}
      </Spacer>
    </Card>
  );
};
